import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { apiUrl, assetUrl } from '../config/api';
import { apiFetch, isLoggedIn } from '../utils/session';
import { themeColor } from '../utils/theme';
import { Link } from 'react-router-dom';
import './DigitalMemberCard.css';

const QR_SIZE = 112;

function DigitalMemberCard() {
  const sheetRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [member, setMember] = useState(null);
  const [selfieSrc, setSelfieSrc] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) {
      setLoading(false);
      setError('Sign in first and open this page from the login screen.');
      return;
    }

    let cancelled = false;
    let objectUrl = '';
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch(apiUrl('/profile/card-data'));
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Could not load your card');
        }
        if (cancelled) return;
        setMember(data);
        if (data.id && data.selfiePath) {
          const photoRes = await apiFetch(apiUrl(`/users/${data.id}/selfie`));
          if (photoRes.ok) {
            const blob = await photoRes.blob();
            if (!cancelled) {
              objectUrl = URL.createObjectURL(blob);
              setSelfieSrc(objectUrl);
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load card');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const verificationUrl = useMemo(() => {
    if (!member?.memberId) return '';
    const fromEnv = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/$/, '');
    const base = fromEnv || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/verify/${encodeURIComponent(member.memberId)}`;
  }, [member]);

  const downloadImage = async () => {
    if (!sheetRef.current || !member) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(sheetRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: themeColor('color-bg') || '#fbf5ea',
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `sangram-sena-id-${member.memberId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      setError(e.message || 'Download failed — try another browser.');
    } finally {
      setDownloading(false);
    }
  };

  const logoSrc = assetUrl('/uploads/logos/logo.png');

  return (
    <div className="dcard-page">
      <div className="dcard-shell">
        <header className="dcard-header">
          <div>
            <h1 className="dcard-title">Digital member ID</h1>
            <p className="dcard-sub">Official volunteer identification — Sangram Sena, Ahmedabad</p>
          </div>
          <Link className="dcard-back" to="/profile">
            ← My profile
          </Link>
        </header>

        {loading ? (
          <p className="dcard-muted">Loading your card…</p>
        ) : error ? (
          <div className="dcard-error" role="alert">
            {error}
          </div>
        ) : member ? (
          <>
            <div className="dcard-actions">
              <button
                type="button"
                className="dcard-download"
                onClick={downloadImage}
                disabled={downloading}
              >
                {downloading ? 'Preparing image…' : 'Download ID card'}
              </button>
            </div>

            <div ref={sheetRef} className="dcard-sheet">
              <section className="dcard-face dcard-face-front" aria-label="ID card front">
                <div className="dcard-front-top">
                  <div className="dcard-logo-block">
                    <img
                      className="dcard-logo"
                      src={logoSrc}
                      alt=""
                      crossOrigin="anonymous"
                    />
                  </div>
                  <div className="dcard-org">
                    <span className="dcard-org-name">Sangram Sena</span>
                    <span className="dcard-org-loc">Ahmedabad</span>
                  </div>
                </div>
                <div className="dcard-front-body">
                  <div className="dcard-fields">
                    <p className="dcard-field-label">Member name</p>
                    <p className="dcard-field-value">{member.name}</p>
                    <p className="dcard-field-label">Role</p>
                    <p className="dcard-field-value">{member.role}</p>
                    <p className="dcard-field-label">Member ID</p>
                    <p className="dcard-field-id">{member.memberId}</p>
                    <p className="dcard-field-label">Area</p>
                    <p className="dcard-field-value">{member.area}</p>
                  </div>
                  <div className="dcard-photo-wrap">
                    {selfieSrc ? (
                      <img className="dcard-photo" src={selfieSrc} alt="" />
                    ) : (
                      <div className="dcard-photo-ph">Photo</div>
                    )}
                  </div>
                </div>
                <div className="dcard-front-strip">Volunteer · Community service</div>
              </section>

              <section className="dcard-face dcard-face-back" aria-label="ID card back">
                <div className="dcard-face-back-inner">
                  <p className="dcard-face-back-title">Verify this member</p>
                  <p className="dcard-face-back-text">
                    Scan the QR code to confirm this ID against our public register.
                  </p>
                  <div className="dcard-qr-wrap">
                    {verificationUrl ? (
                      <QRCodeSVG
                        value={verificationUrl}
                        size={QR_SIZE}
                        level="M"
                        marginSize={2}
                        bgColor="#ffffff"
                        fgColor="#1a1a1a"
                      />
                    ) : null}
                  </div>
                  <p className="dcard-face-back-url">{verificationUrl}</p>
                  <p className="dcard-face-back-foot">
                    Issued by Sangram Sena · Not transferable · Report misuse to your Area Head
                  </p>
                </div>
              </section>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default DigitalMemberCard;
